import React, { useState } from 'react';
import { Button, Flex, Input } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'phosphor-react';
import { toast } from 'sonner';
import { centralApi } from '../../hooks/useCentralPermissoes';
import { AZUL, AZUL_ESCURO } from './ui';

/** Cria um grupo de colaboradores direto na Central e já o abre para configurar. */
export function NovoGrupo({
  nomesExistentes,
  onCriado,
}: {
  nomesExistentes: string[];
  onCriado: (grupoId: number) => void;
}) {
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const limpo = nome.trim();
  const repetido = nomesExistentes.some(n => n.toLowerCase() === limpo.toLowerCase());

  const criar = useMutation({
    mutationFn: () => centralApi.criarGrupo(limpo),
    onSuccess: async grupo => {
      await queryClient.invalidateQueries({ queryKey: ['central'] });
      toast.success(`Grupo ${grupo.nome} criado`, {
        description: 'Agora libere as telas, a carteira e os usuários dele.',
      });
      setNome('');
      setAberto(false);
      onCriado(grupo.id);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Não foi possível criar o grupo'),
  });

  if (!aberto) {
    return (
      <Button
        size="sm"
        variant="outline"
        borderRadius="999px"
        bg="rgba(255,255,255,.7)"
        leftIcon={<Plus size={14} />}
        onClick={() => setAberto(true)}
      >
        Novo grupo
      </Button>
    );
  }

  return (
    <Flex
      as="form"
      gap="8px"
      onSubmit={e => {
        e.preventDefault();
        if (limpo && !repetido) criar.mutate();
      }}
    >
      <Input
        autoFocus
        size="sm"
        borderRadius="10px"
        bg="white"
        placeholder="Nome do grupo"
        aria-label="Nome do grupo novo"
        value={nome}
        isInvalid={repetido}
        onChange={e => setNome(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') setAberto(false);
        }}
      />
      <Button
        type="submit"
        size="sm"
        borderRadius="999px"
        color="white"
        bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
        _hover={{ bg: AZUL_ESCURO }}
        isDisabled={!limpo || repetido}
        isLoading={criar.isPending}
      >
        Criar
      </Button>
    </Flex>
  );
}
